import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';

import { usePasswordPolicy } from '@/shared/api/passwordPolicy';
import { useApiErrorMessage } from '@/shared/i18n/useApiErrorMessage';
import { Button, Field, Skeleton } from '@/shared/ui';

import { activateAccount, checkActivationToken } from '../api/activationApi';
import { AuthLayout } from './AuthLayout';
import styles from './AuthLayout.module.css';

/**
 * Czyta token z **fragmentu** adresu i od razu go z niego usuwa.
 *
 * ⭐ Dwie rzeczy naraz: fragment nie jedzie na serwer (więc nie ma go w logach żądań),
 * a `replaceState` sprawia, że nie zostaje też w historii przeglądarki ani w pasku
 * adresu, z którego bywa kopiowany do czatu (dec. 147, zab. 2).
 */
function useTokenZFragmentu(): string {
  const [token] = useState(() => window.location.hash.replace(/^#/, ''));

  useEffect(() => {
    if (window.location.hash === '') return;

    window.history.replaceState(null, '', window.location.pathname + window.location.search);
  }, []);

  return token;
}

/**
 * Pierwsze wejście administratora klienta — ustawienie hasła z linku aktywacyjnego
 * (dec. 147).
 *
 * ⚠ Ekran ma cztery stany i każdy prowadzi gdzie indziej: sprawdzanie linku ·
 * link martwy (nie ma dokąd iść, trzeba poprosić o nowy) · formularz · sukces.
 * ⛔ Bez optimistic UI: „konto gotowe" pokazane przed odpowiedzią serwera znaczyłoby
 * hasło, którego nie da się użyć (dec. 78).
 */
export function ActivateScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const describeError = useApiErrorMessage();
  const token = useTokenZFragmentu();

  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [touched, setTouched] = useState(false);

  const sprawdzenie = useQuery({
    queryKey: ['auth', 'activation', token],
    queryFn: ({ signal }) => checkActivationToken(token, signal),
    enabled: token !== '',
    retry: false,
    staleTime: Infinity,
  });

  const aktywacja = useMutation({
    mutationFn: () => activateAccount(token, password),
  });

  // ⭐ Minimum z serwera (`GET /api/auth/policy`), nie ze stałej. Zmierzone na produkcji
  //   2026-08-22 przy PIERWSZEJ aktywacji: front mówił „co najmniej 8", host wymagał 10 —
  //   klient wpisał 8, potem 9 znaków i dwa razy przeczytał to samo zdanie. Podpowiedź,
  //   walidacja i komunikat odmowy stoją teraz na jednej liczbie: tej, którą egzekwuje serwer.
  //   Dopóki polityka nie dojedzie (ułamek sekundy, równolegle ze sprawdzeniem linku),
  //   formularz nie udaje, że zna próg — serwer rozstrzyga, a odmowa niesie `args.minLength`.
  const min = usePasswordPolicy().data?.minPasswordLength;

  const passwordError =
    touched && min !== undefined && password.length < min
      ? t('auth.validation.passwordTooShort', { min })
      : undefined;

  const repeatError =
    touched && repeat !== password ? t('auth.activate.passwordsDiffer') : undefined;

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    setTouched(true);

    if ((min !== undefined && password.length < min) || repeat !== password) return;

    aktywacja.mutate();
  }

  const footer = <Link to="/login">{t('auth.registration.backToLogin')}</Link>;

  // ── Sukces ───────────────────────────────────────────────────────────────
  if (aktywacja.isSuccess) {
    return (
      <AuthLayout
        title={t('auth.activate.doneTitle')}
        subtitle={t('auth.activate.doneSubtitle')}
        footer={footer}
      >
        <p className={styles['done']}>{t('auth.activate.done', { login: aktywacja.data })}</p>
        <Button onClick={() => void navigate('/login')}>{t('auth.activate.goToLogin')}</Button>
      </AuthLayout>
    );
  }

  // ── Sprawdzanie linku ────────────────────────────────────────────────────
  if (token !== '' && sprawdzenie.isPending) {
    return (
      <AuthLayout title={t('auth.activate.title')} subtitle={t('auth.activate.checking')}>
        {/* Szkielet, nie spinner — ekran ma od razu wyglądać jak to, czym będzie. */}
        <Skeleton height="3rem" announce />
      </AuthLayout>
    );
  }

  // ── Link martwy ──────────────────────────────────────────────────────────
  // ⚠ JEDEN komunikat na wygasły, zużyty i zmyślony (dec. 147, zab. 9) — rozróżnienie
  //   mówiłoby, czy trafiło się w istniejący link.
  if (token === '' || sprawdzenie.isError || sprawdzenie.data === false) {
    return (
      <AuthLayout
        title={t('auth.activate.deadTitle')}
        subtitle={t('auth.activate.deadSubtitle')}
        footer={footer}
      >
        <p className={styles['notice']}>{t('auth.activate.dead')}</p>
      </AuthLayout>
    );
  }

  // ── Formularz ────────────────────────────────────────────────────────────
  return (
    <AuthLayout
      title={t('auth.activate.title')}
      subtitle={t('auth.activate.subtitle')}
      footer={footer}
    >
      <form className={styles['form']} onSubmit={onSubmit} noValidate>
        <Field
          label={t('auth.activate.passwordField')}
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          {...(passwordError ? { error: passwordError } : {})}
          {...(min !== undefined ? { hint: t('auth.activate.passwordHint', { min }) } : {})}
        />

        <Field
          label={t('auth.activate.repeatField')}
          type="password"
          autoComplete="new-password"
          value={repeat}
          onChange={(event) => setRepeat(event.target.value)}
          {...(repeatError ? { error: repeatError } : {})}
        />

        {aktywacja.isError ? (
          <p className={styles['formError']} role="alert">
            {describeError(aktywacja.error)}
          </p>
        ) : null}

        <Button type="submit" disabled={aktywacja.isPending}>
          {aktywacja.isPending ? t('auth.activate.submitting') : t('auth.activate.submit')}
        </Button>
      </form>
    </AuthLayout>
  );
}
